import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['display', 'input']
  static values = { autosave: { type: Boolean, default: true } }

  connect() {
    this._clickOutside = this._clickOutside.bind(this)
  }

  edit() {
    this.displayTarget.hidden = true
    this.inputTarget.hidden = false
    const field = this.inputTarget.querySelector('input, textarea, select')
    field?.focus()
    document.addEventListener('click', this._clickOutside, true)
    if (field) {
      field.dataset.originalValue = field.value
      if (this.autosaveValue) {
        this._onChange = () => this.save()
        field.addEventListener('change', this._onChange)
      }
    }
  }

  cancelClick(event) {
    event.preventDefault()
    this.cancel()
  }

  commit(event) {
    if (event.target.tagName === 'TEXTAREA') return
    event.preventDefault()
    this.save()
  }

  cancel() {
    const input = this.inputTarget.querySelector('input, textarea, select')
    if (input) {
      input.value = input.dataset.originalValue ?? input.defaultValue
      if (this._onChange) {
        input.removeEventListener('change', this._onChange)
        this._onChange = null
      }
      input.dispatchEvent(new Event('change', { bubbles: false }))
    }
    this.inputTarget.hidden = true
    this.displayTarget.hidden = false
    document.removeEventListener('click', this._clickOutside, true)
  }

  save() {
    const input = this.inputTarget.querySelector('input, textarea, select')
    const originalValue = input?.dataset.originalValue ?? input?.defaultValue

    if (input && input.value === originalValue) {
      this.cancel()
      return
    }

    if (input && this._onChange) {
      input.removeEventListener('change', this._onChange)
      this._onChange = null
    }

    const badge = this.displayTarget.querySelector('.badge')
    const span = badge
      ? badge.querySelector('span')
      : this.displayTarget.querySelector('span')
    const previousText = span?.textContent
    const previousBadgeClass = badge ? [...badge.classList].find(c => c.startsWith('badge--')) : null

    if (input && span) {
      const newText = input.tagName === 'SELECT'
        ? (input.selectedOptions[0]?.text ?? input.value)
        : input.value
      span.textContent = newText
      if (badge && input.tagName === 'SELECT') {
        if (previousBadgeClass) badge.classList.remove(previousBadgeClass)
        badge.classList.add(`badge--${input.value}`)
      }
    }
    this.inputTarget.hidden = true
    this.displayTarget.hidden = false
    document.removeEventListener('click', this._clickOutside, true)

    if (!this.autosaveValue) return

    const form = this.element.closest('form')
    if (!form) return

    this.element.classList.add('inline-edit--saving')

    form.addEventListener('turbo:submit-end', (event) => {
      this.element.classList.remove('inline-edit--saving')
      if (event.detail.success) {
        toastr.success('Saved')
      } else {
        if (span && previousText !== undefined) span.textContent = previousText
        if (badge && previousBadgeClass) {
          const currentBadgeClass = [...badge.classList].find(c => c.startsWith('badge--'))
          if (currentBadgeClass) badge.classList.remove(currentBadgeClass)
          badge.classList.add(previousBadgeClass)
        }
        this.edit()
        toastr.error('Could not save')
      }
    }, { once: true })

    form.requestSubmit()
  }

  disconnect() {
    document.removeEventListener('click', this._clickOutside, true)
  }

  _clickOutside(event) {
    if (this.element.contains(event.target)) return
    if (event.target.closest('.flatpickr-calendar')) return
    this.save()
  }
}
